// React Imports
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ICONS from "../../../../assets/Icons/index.js";
import PrimaryButton from "../../../../components/_utils/CommonButtons/PrimaryButton";
import TopNavComponent from "../../../../components/_utils/CommonTopNav/TopNavComponent";
import CardContainer from "../../../../components/_utils/CommonViewAllCard/CardContainer";
import TextWithIcon from "../../../../components/_utils/CommonViewAllCard/CardSubComponent/TextWithIcon";
import PaginationComponent from "../../../../components/_utils/PaginationComponent/index.jsx";
import SVGComponent from "../../../../components/_utils/SVGComponent/index.jsx";
import Loader from "../../../../components/Loader/Index";
import useServiceChecker from "../../../../components/ServiceChecker";
import { getAllCategories } from "../../../../redux/features/sharedResources/sharedResources.js";
import {
  GlobalConfiguration,
  PaginationConfiguration,
} from "../../../../utils/configurationProvider";
import useCategories from "../../../../utils/useCategories.js";
import useCustomSearchParams from "../../../../utils/useCustomSearchParams";
import useMultiSelect from "../../../../utils/useMultiSelect.js";

const CustomCategoryViewAll = () => {
  const scrollContainerRef = useRef(null);

  // React hooks
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Custom hooks
  const { handleChange } = useMultiSelect();
  const [ensureAll] = useServiceChecker();
  const categoryMapper = useCategories();
  const hasCTransactionCategory = ensureAll(["CTransactionCategory"]);

  // States
  const [showInnerSearchBar, setShowInnerSearchBar] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [active, setActive] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [labelAccounts, setLabelAccounts] = useState([]);
  const { searchParam, handleSearchParams, handleClearSearchParam } =
    useCustomSearchParams({});
  const [searchString, setSearchString] = useState(
    searchParam.get("search") || location?.state?.filteredData?.search || "",
  );
  const [sortValue, setSortValue] = useState(
    searchParam.get("sort") || location?.state?.filteredData?.sort || "",
  );
  const [typeValue, setTypeValue] = useState(
    searchParam.get("type") || location?.state?.filteredData?.type || "",
  );
  const [statusValue, setStatusValue] = useState(
    searchParam.get("status") || location?.state?.filteredData?.status || "",
  );
  const [categoryData, setCategoryData] = useState([]);
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState(
    Number(
      searchParam.get("pagesize") || PaginationConfiguration.paginationSmall,
    ),
  );
  const [pageSize, setPageSize] = useState(
    Number(
      searchParam.get("pagesize") ||
        location?.state?.filteredData?.pagesize ||
        PaginationConfiguration.paginationSmall,
    ),
  );
  const [currentPage, setCurrentPage] = useState(
    Number(
      searchParam.get("pageno") ||
        location?.state?.filteredData?.pageno ||
        GlobalConfiguration.lengthCheckOne,
    ),
  );
  const [clearSearchQuery, setClearSearchQuery] = useState(false);
  const dropdownOptions = PaginationConfiguration.paginationOptions;
  const startIndex = (currentPage - 1) * pageSize;
  const currentPageData = categoryData.slice(startIndex, startIndex + pageSize);
  const categorySort = [
    {
      name: "Name : A to Z",
      default: true,
    },
    {
      name: "Name : Z to A",
    },
    {
      name: "Date : New to old",
    },
    {
      name: "Date : Old to new",
    },
  ];

  const sortByMapping = {
    "Name : A to Z": "ascending",
    "Name : Z to A": "descending",
    "Date : New to old": "latest",
    "Date : Old to new": "oldest",
  };
  const sortBy = sortByMapping[sortValue] || "ascending";

  const [isCategoryUIRendering, setIsCategoryUIRendering] = useState(false);

  const handleSubmit = (key, value) => {
    handleSearchParams(key, value);
  };
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleSelect = (value) => {
    const newPageSize = Number(value);
    if (newPageSize === pageSize) {
      toggleDropdown();
      return;
    }
    setIsLocalLoading(true);
    handleSubmit("pagesize", newPageSize);
    if (
      newPageSize === PaginationConfiguration.paginationSmall &&
      labelAccounts?.length > PaginationConfiguration.paginationSmall
    ) {
      handleChange(labelAccounts, setLabelAccounts, newPageSize, labelAccounts);
    }
    setSelectedValue(newPageSize);
    setPageSize(newPageSize);
    setCurrentPage(GlobalConfiguration.lengthCheckOne);
    handleSubmit("pageno", GlobalConfiguration.lengthCheckOne);
    toggleDropdown();
    handleScrollToTop();
    requestAnimationFrame(() => {
      setIsLocalLoading(false);
    });
  };

  const handleClearParams = (e) => {
    setClearSearchQuery(true);
    handleClearSearchParam();

    if (showInnerSearchBar) return;
    setSearchString("");
    setSearchText("");
    setSortValue("");
    setStatusValue("");
    setTypeValue("");
  };

  const handleScrollToTop = () => {
    scrollContainerRef.current?.scrollTo({
      top: GlobalConfiguration.lengthCheckZero,
      behavior: "auto",
    });
  };

  const handlePagination = (page) => {
    const newPage = Number(page);
    if (newPage === currentPage) {
      return;
    }
    setIsLocalLoading(true);
    handleSubmit("pageno", newPage);
    setCurrentPage(newPage);
    handleScrollToTop();
    requestAnimationFrame(() => {
      setIsLocalLoading(false);
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLocalLoading(true);
      setIsCategoryUIRendering(true);
      try {
        const response = await dispatch(
          getAllCategories({
            search_string: searchString,
            sort_by: sortBy,
            category_status: statusValue,
            category_type: typeValue,
          }),
        );
        setCategoryData(response?.payload?.categories ?? []);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        setCategoryData([]);
      } finally {
        setIsLocalLoading(false);
      }
    };

    fetchData();
  }, [searchString, sortValue, statusValue, typeValue, sortBy, dispatch]);

  useEffect(() => {
    return () => {
      const nextPath = window.location.pathname;
      if (
        nextPath.startsWith("/settings/category_dashboard/") ||
        nextPath === "/home"
      ) {
        return;
      }
      handleClearParams();
      dispatch(getAllCategories());
    };
  }, []);

  const totalCount = categoryData.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const paginationData = {
    total_count: totalCount,
    title: {
      total_pages: totalPages,
      page_item_count: pageSize,
      current_page: currentPage,
    },
  };

  useEffect(() => {
    if (isLocalLoading) return;
    const frame = requestAnimationFrame(() => {
      setIsCategoryUIRendering(false);
    });
    return () => cancelAnimationFrame(frame);
  }, [isLocalLoading]);

  useEffect(() => {
    if (clearSearchQuery) {
      navigate(
        {
          pathname: location?.pathname,
          search: "",
        },
        { replace: true },
      );

      return;
    }

    const filters = location.state?.filteredData;

    if (!filters) return;

    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        params.set(key, String(value));
      }
    });

    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      {
        replace: true,
      },
    );
  }, [clearSearchQuery, location.state, location.pathname, navigate]);

  return (
    <>
      <div className="h-full-height flex flex-col overflow-y-hidden">
        {showInnerSearchBar && (
          <div className="h-full-height w-full-width fixed bg-PopUp-BackDrop opacity-dark-opacity z-z-index-overlay"></div>
        )}
        <>
          <div className="relative z-z-index-overlay">
            <div className="h-navbar-breadcrumb-height Qmd:block hidden bg-primary-nav"></div>
            <TopNavComponent
              topNavCtaBtnWithIcon1Props={{
                btnText: "Add category",
                btnType: "button",
                handleFunction: () => {
                  if (isLocalLoading || isCategoryUIRendering) return;
                  if (hasCTransactionCategory.status === true) {
                    navigate("/settings/category_dashboard/add_category", {
                      state: {
                        filteredData: {
                          status: statusValue,
                          type: typeValue,
                          sort: sortValue,
                          search: searchString,
                          pageno: currentPage,
                          pagesize: pageSize,
                        },
                      },
                    });
                  } else {
                    navigate("/feature_not_assigned", {
                      state: hasCTransactionCategory.unavailableService,
                    });
                  }
                },
                isDisabled:
                  !hasCTransactionCategory.status ||
                  isLocalLoading ||
                  isCategoryUIRendering,
                enabledTextColor: "var(--primary-bg)",
                disabledTextColor: null,
                enabledBgColor: "var(--text-primary)",
                disabledBgColor: null,
                isLoading: isLocalLoading || isCategoryUIRendering,
                outLineColor: null,
                hoverOption: "black",
                buttonRef: null,
                fullWidthRequired: null,
                inActiveVariantNeeded: null,
                isLoaderNeeded: null,
                icon: ICONS?.ADD_BUTTON_WHITE,
                imgColor: "var(--primary-bg)",
                iconToBeAlign: "right",
              }}
              filteredSearchProps={{
                onSearchInput: (e) => setSearchText(e),
                searchInputRef: null,
                searchText: searchText,
                searchPlaceholder: "Search in Category",
                filterPlaceholder: "Category name / description",
                isSearchDisabled: isLocalLoading,
                filterItems: [
                  {
                    name: "Search",
                    isMulti: false,
                    value: searchString,
                    setValue: (val) => {
                      setCurrentPage(GlobalConfiguration?.lengthCheckOne)                     
                      if (!val) {
                        setClearSearchQuery(true);
                      }
                      handleSubmit("search", val);
                      setSearchString(val);
                    },
                    isDropdown: false,
                    isDisabled: isLocalLoading,
                  },
                ],
                filters: [
                  {
                    name: "Category status",
                    filterType: "dropdown",
                    variant: "single",
                    size: "small",
                    value: statusValue,
                    dropdownPlaceHolder: "Select status",
                    isDisabled: isLocalLoading,
                    options: ["active", "inactive"],
                    statusPop: true,
                    handleChange: (val) => {
                      setCurrentPage(GlobalConfiguration?.lengthCheckOne)
                      if (!val) {
                        setClearSearchQuery(true);
                      }
                      handleSubmit("status", val);
                      setStatusValue(val);
                    },
                  },

                  {
                    name: "Category type",
                    filterType: "dropdown",
                    variant: "single",
                    dropdownPlaceHolder: "Select type",
                    isDisabled: isLocalLoading,
                    size: "small",
                    value: typeValue,
                    options: ["custom", "default"],
                    handleChange: (val) => {
                      setCurrentPage(GlobalConfiguration?.lengthCheckOne)
                      if (!val) {
                        setClearSearchQuery(true);
                      }
                      handleSubmit("type", val);
                      setTypeValue(val);
                    },
                  },
                ],
                loading: isLocalLoading,
                inputData: [],
                setShowInnerSearchBar: setShowInnerSearchBar,
                setActive: setActive,
                isShowSortBy: true,
                isShowFilter: true,
                showSearchBar: false,
                isFilterClearAllDisabled: !(statusValue || typeValue),
                isSearchClearAllDisabled: null,
                sortByFilter: categorySort,
                active: active,
                sortValue: sortValue,
                sortHandleChange: (val) => {
                  setCurrentPage(GlobalConfiguration?.lengthCheckOne)
                  if (!val) {
                    setClearSearchQuery(true);
                  }
                  handleSubmit("sort", val);
                  setSortValue(val);
                },
                isSortDefaultDisabled:
                  sortValue === "" ||
                  sortValue === "ascending" ||
                  isLocalLoading,
                isSortDisabled: isLocalLoading,
                isFilterDisabled: isLocalLoading,
              }}
              ispagination={
                categoryData?.length <= GlobalConfiguration.lengthCheckZero || isLocalLoading
                  ? false
                  : true
              }
            />
          </div>
          {isLocalLoading ? (
            <>
              <div className="grid place-items-center w-full h-full">
                <Loader />
              </div>
            </>
          ) : categoryData?.length > GlobalConfiguration?.lengthCheckZero ? (
            <>
              <div
                ref={scrollContainerRef}
                className={`${showInnerSearchBar ? "overflow-hidden" : "overflow-y-scroll scroll-pb-[calc(var(--stepper-moble-height)+var(--padding-md))] Qmd:scroll-pb-top_md scroll-pt-top_md  Qmd:pb-padding-base"} h-full `}
              >
                {isLocalLoading && (
                  <div className="absolute inset-0 z-z-index-overlay grid place-items-center">
                    <Loader />
                  </div>
                )}
                <div
                  className={`${showInnerSearchBar ? "overflow-y-hidden" : ""} flex-1  relative z-z-index-content`}
                >
                  <div className="standard-card-alignment gap-y-gap-md gap-x-gap-sm overflow-y-visible items-center grow-0 shrink-0 min-h-0 h-full">
                    {currentPageData.map(
                      ({ name, active, custom_category, description, id }) => {
                        const categoryIcon = categoryMapper(id);
                        return (
                          <CardContainer
                            cardBgColor="primary-card"
                            context={id}
                            onClickHandler={(e, c) => {
                              const isViewAll = location.pathname.includes(
                                `settings/category_dashboard`,
                              );

                              navigate(`view_details/${c}`, {
                                state: {
                                  isViewAll,
                                  filteredData: {
                                    status: statusValue,
                                    type: typeValue,
                                    sort: sortValue,
                                    search: searchString,
                                    pageno: currentPage,
                                    pagesize: pageSize,
                                  },
                                },
                              });
                            }}
                          >
                            <card-body>
                              <TextWithIcon
                                text={name}
                                textColor="var(--text-primary)"
                                subText="Category name"
                              />

                            <div className="flex gap-x-gap-sm justify-between items-center">
                              <div className="flex flex-1 truncate flex-col gap-y-gap-xs">
                                <p
                                  className={`text-font-sm flex-1 truncate font-semibold pl-mobile-padding-l Qmd:pl-padding-sm ${
                                    active === "Y"
                                      ? "text-settled"
                                      : "text-failed"
                                  }`}
                                >
                                  {active === "Y" ? "Active" : "Inactive"}
                                </p>

                                <div className="w-full relative flex pl-mobile-padding-l Qmd:pl-padding-sm justify-between items-center">
                                  <p className="text-font-xs text-text-secondary flex-1 truncate font-normal">
                                    Category status
                                  </p>
                                </div>
                              </div>

                                <div className="bg-text-primary flex items-center gap-x-gap-sm rounded-l-border-radius-md py-padding-xs px-padding-sm">
                                    {categoryIcon?.isSvg ? (
                                      <SVGComponent
                                        url={categoryIcon?.url}
                                        height="var(--icon-sm)"
                                        width="var(--icon-sm)"
                                        color={categoryIcon?.colour}
                                        alt={name}
                                      />
                                    ) : (
                                      <img
                                        className="w-icon-sm h-icon-sm"
                                        src={categoryIcon?.url}
                                        alt={name}
                                      />
                                    )}
                                  </div>
                                </div>                             
                              <TextWithIcon
                                text={
                                  custom_category === "Y" ? "Custom" : "Default"
                                }
                                textColor="var(--text-primary)"
                                subText="Category type"
                              />
                              <TextWithIcon
                                text={description ? description : "Not given"}
                                textColor="var(--text-primary)"
                                subText="Description"
                              />
                            </card-body>
                          </CardContainer>
                        );
                      },
                    )}
                  </div>
                </div>             
          <div className={`${showInnerSearchBar ? "" : "Qmd:z-z-index-content z-z-index-overlay "} fixed bottom-bottom-base w-full`}>
                <PaginationComponent
                  setIsOpen={setIsOpen}
                  handleSelect={handleSelect}
                  selectedValue={selectedValue}
                  pageSize={pageSize}
                  dropdownOptions={dropdownOptions}
                  handlePagination={handlePagination}
                  currentPage={currentPage}
                  data={paginationData}
                />
          </div>
          </div>
            </>
          ) : (
            <>
              <div
                className={
                  showInnerSearchBar
                    ? `h-height-full  opacity-dark-opacity flex flex-col items-center justify-center Qmd:m-auto mx-auto my-margin-xl gap-gap-sm`
                    : `flex flex-col items-center justify-center Qmd:m-auto mx-auto my-margin-xl h-height-full gap-gap-sm ${
                        false ? "h-auto" : ""
                      }`
                }
              >
                <img
                  src={ICONS?.NO_DATA_FOUND}
                  alt="No accounts found"
                  className={"w-graphic-width-md h-graphic-rectangle-height-md"}
                />
                <p className={"text-font-sm text-text-primary items-center"}>
                  No data found.
                </p>
                {searchString.length ||
                searchText.length ||
                statusValue.length ||
                typeValue.length ||
                sortValue.length ? (
                  <div>
                    <PrimaryButton
                      isDisabled={showInnerSearchBar}
                      enabledBgColor="var(--primary-act)"
                      btnText="Clear parameters"
                      enabledTextColor="var(--text-primary)"
                      handleFunction={handleClearParams}
                      buttonRef={null}
                      hoverOption="white"
                    />
                  </div>
                ) : null}
            </div>
          </>
        )}
      <div className={`h-topnav-bottom-height w-full Qmd:h-pagination-height shrink-0 grow-0 `}></div>
        </>
      </div>
    </>
  );
};

export default CustomCategoryViewAll;
